import {Box, Button, Paper, Stack, Typography} from "@mui/material";
import {useState} from "react";
import UserInfoDrawer from "./UserInfoDrawer";

const CONNECTOR_COLOR = "#9e9e9e";
const CONNECTOR_WIDTH = 2;

// ---------- 节点卡片 ----------
function NodeCard({node, variant = "descendant", onViewUser, onToggleExpand, expanded, loadingIds = new Set()}) {
  const isCurrent = variant === "current";
  const isLoading = loadingIds.has(node.id);
  const hasChildren = node.childCount > 0;
  const canExpand = hasChildren && !expanded;

  const bgColors = {
    current: "#e3f2fd",
    descendant: "#ffffff",
  };

  return (
    <Stack alignItems="center" sx={{position: "relative"}}>
      <Paper
        sx={{
          p: 1,
          minWidth: 160,
          bgcolor: bgColors[variant],
          border: isCurrent ? "2px solid #1976d2" : "1px solid #e0e0e0",
          borderRadius: 1.5,
        }}
      >
        <Stack spacing={0.3}>
          <Typography
            variant="body2"
            fontWeight={isCurrent ? 700 : 400}
            noWrap
            onClick={onViewUser ? () => onViewUser(node) : undefined}
            sx={{
              cursor: onViewUser ? "pointer" : "default",
              "&:hover": onViewUser ? {color: "primary.main", textDecoration: "underline"} : {},
            }}
          >
            {node.user_nickname || "-"}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{userSelect: "text"}}>
            金银通ID：{node.id} | 鲸世优选UID：{node.plat_id || "-"}
          </Typography>
          <Typography variant="caption" sx={{userSelect: "text"}}>
            <Box component="span" color="primary.main">产出金豆：{Number(node.income || 0).toFixed(2)}</Box>
            <Box component="span" color="text.secondary" mx={0.5}>|</Box>
            <Box component="span" color="secondary.main">充值金豆：{Number(node.coin_recharge || 0).toFixed(2)}</Box>
          </Typography>
        </Stack>
      </Paper>

      {/* 展开/收回按钮 */}
      {canExpand && (
        <Button
          size="small"
          variant="outlined"
          disabled={isLoading}
          onClick={(e) => {
            e.stopPropagation();
            if (onToggleExpand) onToggleExpand(node.id, true);
          }}
          sx={{mt: 0.5, fontSize: 11, py: 0, minHeight: 22, minWidth: "auto"}}
        >
          {isLoading ? "加载中..." : `展开 ${node.childCount} 人`}
        </Button>
      )}
      {expanded && hasChildren && (
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            if (onToggleExpand) onToggleExpand(node.id, false);
          }}
          sx={{mt: 0.5, fontSize: 11, py: 0, minHeight: 22, minWidth: "auto", color: "text.secondary"}}
        >
          收回
        </Button>
      )}
    </Stack>
  );
}

// ---------- 竖线连接器 ----------
function VLine({height = 20}) {
  return <Box sx={{width: CONNECTOR_WIDTH, height, bgcolor: CONNECTOR_COLOR, flexShrink: 0}} />;
}

// ---------- 直推下级主视图 ----------
function DescendantView({current, children, onViewUser, onToggleExpand, expandedIds, loadingIds}) {
  return (
    <Stack alignItems="center" sx={{py: 2}}>
      <NodeCard node={current} variant="current" onViewUser={onViewUser} />
      {children && children.length > 0 && <VLine height={16} />}
      {children && children.length > 0 && (
        <DescendantTree
          children={children}
          onViewUser={onViewUser}
          onToggleExpand={onToggleExpand}
          expandedIds={expandedIds}
          loadingIds={loadingIds}
        />
      )}
    </Stack>
  );
}

// ---------- 下级树（横向展开，带连线） ----------
function DescendantTree({children, onViewUser, onToggleExpand, expandedIds, loadingIds}) {
  if (!children || children.length === 0) return null;

  const hasMultiple = children.length > 1;

  return (
    <Stack direction="row" justifyContent="center">
      {children.map((node, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === children.length - 1;
        const isExpanded = expandedIds.has(node.id);
        const hasVisibleChildren = isExpanded && node.children && node.children.length > 0;

        return (
          <Stack key={node.id} alignItems="center" sx={{position: "relative", minWidth: 170, px: 0.5}}>
            {/* 横线连接 */}
            {hasMultiple && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: isFirst ? "50%" : 0,
                  right: isLast ? "50%" : 0,
                  height: CONNECTOR_WIDTH,
                  bgcolor: CONNECTOR_COLOR,
                }}
              />
            )}

            <VLine height={16} />

            <NodeCard
              node={node}
              variant="descendant"
              onViewUser={onViewUser}
              onToggleExpand={onToggleExpand}
              expanded={isExpanded}
              loadingIds={loadingIds}
            />

            {hasVisibleChildren && <VLine height={12} />}
            {hasVisibleChildren && (
              <DescendantTree
                children={node.children}
                onViewUser={onViewUser}
                onToggleExpand={onToggleExpand}
                expandedIds={expandedIds}
                loadingIds={loadingIds}
              />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
}

// ---------- 主组件 ----------
export default function ReferralTree({data, onLoadChildren}) {
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [previewUid, setPreviewUid] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!data) {
    return (
      <Box sx={{p: 3, textAlign: "center"}}>
        <Typography color="text.secondary">暂无推荐关系数据</Typography>
      </Box>
    );
  }

  const {current, children = []} = data;

  if (!current) {
    return (
      <Box sx={{p: 3, textAlign: "center"}}>
        <Typography color="text.secondary">该用户不是金银通用户</Typography>
      </Box>
    );
  }

  const handleViewUser = (node) => {
    if (!node.plat_id) return;
    setPreviewUid(node.plat_id);
    setPreviewOpen(true);
  };

  const handleToggleExpand = async (jytId, expand) => {
    if (expand) {
      setExpandedIds((prev) => new Set(prev).add(jytId));
      setLoadingIds((prev) => new Set(prev).add(jytId));
      try {
        if (onLoadChildren) {
          await onLoadChildren(jytId, 1);
        }
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(jytId);
          return next;
        });
      }
    } else {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(jytId);
        return next;
      });
      if (onLoadChildren) {
        onLoadChildren(jytId, -1);
      }
    }
  };

  return (
    <Box sx={{overflowX: "auto"}}>
      <DescendantView
        current={current}
        children={children}
        onViewUser={handleViewUser}
        onToggleExpand={handleToggleExpand}
        expandedIds={expandedIds}
        loadingIds={loadingIds}
      />

      {previewOpen && previewUid && (
        <UserInfoDrawer
          open
          uid={previewUid}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </Box>
  );
}
